import React from 'react';
import { Box, BoxProps } from '@mui/material';

/**
 * FlexContainer Props
 */
export interface FlexContainerProps extends Omit<BoxProps, 'display'> {
  /** Flex direction */
  direction?: 'row' | 'column';
  
  /** Justify content */
  justify?: 'flex-start' | 'flex-end' | 'center' | 'space-between' | 'space-around' | 'space-evenly';
  
  /** Align items */
  align?: 'flex-start' | 'flex-end' | 'center' | 'baseline' | 'stretch';
  
  /** Gap between items (in px or MUI spacing units) */
  gap?: number | string;
  
  /** Allow wrapping */
  wrap?: boolean;
  
  /** Children */
  children: React.ReactNode;
}

/**
 * FlexContainer - Atomic flex layout component
 * 
 * Provides a consistent flex container abstraction over MUI Box.
 * Use this in domain components instead of MUI Stack/Box directly.
 * 
 * @example
 * ```tsx
 * <FlexContainer direction="row" justify="space-between" gap={2}>
 *   <Child1 />
 *   <Child2 />
 * </FlexContainer>
 * ```
 */
export function FlexContainer({
  direction = 'row',
  justify = 'flex-start',
  align = 'center',
  gap = 0,
  wrap = false,
  children,
  sx,
  ...boxProps
}: FlexContainerProps) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: direction,
        justifyContent: justify,
        alignItems: align,
        gap,
        flexWrap: wrap ? 'wrap' : 'nowrap',
        ...sx,
      }}
      {...boxProps}
    >
      {children}
    </Box>
  );
}

export default FlexContainer;
